// Propositions (docs/04 §15): offered in the intermission every DEAL_EVERY-th time and after each
// Reckoning, once G_PROPOSITIONS is owned. The first one ever is story-forced (The Loan).
import { B } from '../content/balance';
import type { DealCtx, DealDef } from '../content/defs';
import { DEAL_LIST, DEALS, SYMBOLS } from '../content/registry';
import { giveCharm, startNightShift } from './grants';
import { say } from './dialogue';
import { newInstance, removeFromStrips } from './grid';
import { clearHexes } from './hex';
import { setHunger } from './hunger';
import type { Modifiers } from './modifiers';
import { stream, type Rng } from './rng';
import { tribute } from './tribute';
import type { DealOffer, GameEvent, GameState, RunState } from './types';

export function dealsUnlocked(mods: Modifiers): boolean {
  return mods.features.has('deals');
}

/** Whether this intermission carries a Proposition. */
export function dealDue(state: GameState, mods: Modifiers, afterReckoning: boolean): boolean {
  if (!dealsUnlocked(mods)) return false;
  if (!state.meta.flags.firstDeal) return true;
  if (afterReckoning) return true;
  const count = state.run!.flags.intermissions ?? 0;
  return count > 0 && count % B.DEAL_EVERY === 0;
}

function eligible(state: GameState, mods: Modifiers): DealDef[] {
  const run = state.run!;
  const ctx = { mods, meta: state.meta, hasShop: mods.features.has('shop') };
  return DEAL_LIST.filter((d) => !run.deals.includes(d.id) && (!d.requires || d.requires(ctx)));
}

export function rollDeal(state: GameState, mods: Modifiers, rng: Rng): DealOffer | null {
  if (!state.meta.flags.firstDeal) {
    const story = DEAL_LIST.find((d) => d.story);
    if (story) return { offers: [story.id], done: false, forced: true };
  }
  const pool = eligible(state, mods);
  const covenant = pool.find((d) => d.covenant);
  const rest = pool.filter((d) => !d.covenant && !d.story);
  const offers: string[] = covenant ? [covenant.id] : [];
  while (offers.length < mods.dealChoices && rest.length) {
    const pick = rest.splice(rng.int(rest.length), 1)[0]!;
    offers.push(pick.id);
  }
  return offers.length ? { offers, done: false } : null;
}

function dealCtx(state: GameState, mods: Modifiers, events: GameEvent[]): DealCtx {
  const run = state.run!;
  const rng = stream(run.rng, 'shop');
  // Twin: the duplicated symbol is spared by its own fine print ("3 random *other* symbols").
  let duplicated: string | null = null;
  return {
    nextTribute: tribute(run.window.n + 1, mods),
    addCoins: (amount) => {
      run.coins = run.coins.add(amount.floor());
      events.push({ e: 'CHARM_TRIGGER', id: 'deal', kind: 'coins', amount: amount.floor().toString() });
    },
    addSymbol: (defId, count) => {
      for (let i = 0; i < count; i++) {
        const inst = newInstance(run, defId);
        run.deck.push(inst);
        events.push({ e: 'SYMBOL_ADDED', uid: inst.uid, defId, by: 'deal' });
      }
    },
    duplicateBest: (count) => {
      const value = (uid: number) => {
        const inst = run.deck.find((d) => d.uid === uid)!;
        return (SYMBOLS.get(inst.defId)?.baseValue ?? 0) + inst.bonusValue;
      };
      const best = [...run.deck].sort((a, b) => value(b.uid) - value(a.uid))[0];
      if (!best) return null;
      duplicated = best.defId;
      for (let i = 0; i < count; i++) {
        const inst = newInstance(run, best.defId);
        inst.bonusValue = best.bonusValue;
        run.deck.push(inst);
        events.push({ e: 'SYMBOL_ADDED', uid: inst.uid, defId: best.defId, by: 'deal' });
      }
      return best.defId;
    },
    destroyRandom: (count, by) => {
      for (let i = 0; i < count && run.deck.length > B.MIN_DECK; i++) {
        const pool = run.deck.map((d, k) => k).filter((k) => run.deck[k]!.defId !== duplicated);
        if (!pool.length) break;
        const idx = rng.pick(pool);
        const [inst] = run.deck.splice(idx, 1);
        removeFromStrips(run, inst!.uid);
        run.stats.destroyed++;
        events.push({ e: 'SYMBOL_DESTROYED', uid: inst!.uid, defId: inst!.defId, by });
      }
    },
    setHunger: (v) => setHunger(state, mods, v, events),
    addDoom: (n) => {
      run.doom = Math.min(mods.doomMax, run.doom + n);
      events.push({ e: 'DOOM', value: run.doom, noticed: false });
    },
    clearHexes: () => clearHexes(state, 'deal', events),
    giveCharm: (rarity) => giveCharm(state, mods, rarity, rng, 'deal', events),
    startNightShift: (spins) => startNightShift(state, mods, spins, events),
    flag: (key, value) => void (run.flags[key] = value),
    nextWindowSpins: (delta) => void (run.nextWindow.spins += delta),
  };
}

export function acceptDeal(state: GameState, mods: Modifiers, id: string, events: GameEvent[]): string | undefined {
  const run = state.run;
  const offer = run?.intermission?.deal;
  if (!run || !offer || offer.done) return 'no proposition';
  if (!offer.offers.includes(id)) return 'not offered';
  const def = DEALS.get(id)!;
  const ctx = dealCtx(state, mods, events);
  def.boon.now?.(ctx);
  // The Covenant: no fine print on any deal this run.
  if (!run.flags.covenant) def.fine.now?.(ctx);
  run.deals.push(id);
  offer.done = true;
  offer.accepted = id;
  const meta = state.meta;
  meta.lifetime.dealsAccepted++;
  meta.flags.firstDeal = true;
  if (def.covenant) meta.flags.covenant = true;
  if (run.flags.freeShop && run.intermission?.shop) {
    run.intermission.shop.freeAll = true;
    delete run.flags.freeShop;
  }
  events.push({ e: 'DEAL_ACCEPTED', id });
  say(state, def.covenant ? 'covenant' : 'dealAccept', events, undefined, { force: true });
  return undefined;
}

export function refuseDeal(state: GameState, events: GameEvent[]): string | undefined {
  const run = state.run;
  const offer = run?.intermission?.deal;
  if (!run || !offer || offer.done) return 'no proposition';
  if (state.meta.flags.covenant) return 'the covenant forbids it';
  offer.done = true;
  const meta = state.meta;
  const forced = !!offer.forced;
  meta.lifetime.dealsRefused++;
  meta.flags.firstDeal = true;
  const count = meta.lifetime.dealsRefused;
  events.push({ e: 'DEAL_REFUSED', id: offer.offers[0] ?? '', count });
  say(state, forced ? 'loanRefused' : count >= B.REFUSE_HABIT ? 'dealRefuseHabit' : 'dealRefuse', events, { n: count }, { force: true });
  return undefined;
}

/** Deal lasting effects for computeModifiers (fine print skipped under the Covenant). */
export function applyDealMods(m: Modifiers, run: RunState): void {
  for (const id of run.deals) {
    const def = DEALS.get(id);
    if (!def) continue;
    def.boon.mods?.(m);
    if (!run.flags.covenant) def.fine.mods?.(m);
  }
}
